import {
  IDirectoryNode,
  useAddDocumentPermissionToUserMutation,
  useDeleteDocumentPermissionFromUserMutation,
  useGetDocumentPermittedUsersQuery,
  useUpdateNodeMutation,
} from 'app/services/documents';
import { IUser, useGetUsersQuery, UserRoleEnum } from 'app/services/user';
import { SearchIcon, ShareFolderIcon } from 'assets/icons';
import { CustomButton } from 'Components/Auth/CustomButton';
import Button from 'Components/Buttons/Button';
import CheckboxInput from 'Components/Form/Inputs/CheckboxInput';
import MultiSelectSearchDropDown from 'Components/Form/SearchDropDown/MultiSelectSearchDropDown';
import { errorToast, successToast } from 'Components/Notifications';
import OverlayWindow from 'Components/OverlayWindow';
import Pill from 'Components/Pills';
import { useCurrentPopup } from 'Components/Popups';
import Spinner from 'Components/Spinner';
import { useEffect, useState } from 'react';
import { useAuth } from 'utils/AuthProvider';

const ShareModal = () => {
  const { userId, isClient } = useAuth();

  const [UpdateNode] = useUpdateNodeMutation();

  const [addDocumentPermissionToUser] =
    useAddDocumentPermissionToUserMutation();
  const [deleteDocumentPermissionFromUser] =
    useDeleteDocumentPermissionFromUserMutation();

  const [enablePublicShare, setEnablePublicShare] = useState<boolean>(true);
  const [selectedUsersList, setSelectedUsersList] = useState<IUser[]>([]);
  const [initialPremittedUsers, setInitialPermittedUsers] = useState<
    Set<number>
  >(new Set());

  const {
    data: users,
    isLoading: isLoadingUsers,
    isSuccess: isSuccessUsers,
  } = useGetUsersQuery({
    roles: isClient
      ? [UserRoleEnum.ACCOUNTANT, UserRoleEnum.ADMIN]
      : [UserRoleEnum.ACCOUNTANT, UserRoleEnum.ADMIN, UserRoleEnum.CLIENT],
  });
  const { contextData, close: closePopup } = useCurrentPopup<IDirectoryNode>();
  const document = contextData as IDirectoryNode;
  const resourceType = document.object_type;
  const { data: permittedUsers, isLoading: isLoadingPermittedUsers } =
    useGetDocumentPermittedUsersQuery({
      resourceType,
      resourceId: document.id,
      permissionType: 'read',
    });

  const publicURL = `${process.env.REACT_APP_FRONT_END_DOMAIN}/${resourceType}/${document.id}/public`;

  let usersOptions: IUser[] = [];

  if (isSuccessUsers && users) {
    usersOptions = users.filter((uesr: IUser) => uesr.id !== userId);
  }

  useEffect(() => {
    if (!isLoadingPermittedUsers) {
      let usersIDSet = new Set<number>();
      permittedUsers.users.forEach((user: IUser) => {
        usersIDSet.add(user.id);
      });
      setSelectedUsersList(permittedUsers.users);
      setInitialPermittedUsers(usersIDSet);
    }
  }, [isLoadingPermittedUsers]);

  const userSearchFilter = (search: string, list: IUser[]) => {
    const needle = search.toLocaleLowerCase().trim();
    return list.filter((u) => {
      return (
        u?.email?.toLocaleLowerCase().includes(needle) ||
        // u.family_name.toLocaleLowerCase().includes(needle) ||
        u?.given_name?.toLocaleLowerCase().includes(needle)
      );
    });
  };

  const addUsers = (cahnges: IUser[] | undefined) => {
    if (cahnges) {
      setSelectedUsersList(cahnges);
    }
  };

  const removeUser = (id: number) => {
    const remainingUsers = selectedUsersList.filter((user) => user.id !== id);
    setSelectedUsersList(remainingUsers);
  };

  const editPermissions = async () => {
    let selectedUsersSet = new Set();
    selectedUsersList.forEach((user) => {
      selectedUsersSet.add(user.id);
    });

    try {
      await Promise.all(
        selectedUsersList.map(async (user) => {
          if (!initialPremittedUsers?.has(user.id)) {
            await addDocumentPermissionToUser({
              permission_type: 'read',
              resource_type: resourceType,
              resource_id: document.id,
              user_id: user.id,
            });
          }
        }),
      );

      await Promise.all(
        permittedUsers?.users.map(async (user: IUser) => {
          if (!selectedUsersSet.has(user.id)) {
            await deleteDocumentPermissionFromUser({
              permission_type: 'read',
              resource_type: resourceType,
              resource_id: document.id,
              user_id: user.id,
            });
          }
        }),
      );

      successToast('Successfull Operation');
      closePopup();
    } catch (error) {
      errorToast("Couldn't grant access to selected users");
    }
  };

  const copyShareableLink = async () => {
    successToast('Copied to clipboard');
    navigator.clipboard.writeText(publicURL);

    await UpdateNode({
      id: document.id,
      is_public: true,
    });
  };

  const updateSharableState = async () => {
    if (enablePublicShare) {
      setEnablePublicShare(!enablePublicShare);

      await UpdateNode({
        id: document.id,
        is_public: false,
      });
    } else {
      setEnablePublicShare(!enablePublicShare);
    }
  };

  return (
    <OverlayWindow
      title={'Share Folder With People'}
      subTitle="Give access for internal user."
      onClose={closePopup}
    >
      {isLoadingUsers || isLoadingPermittedUsers ? (
        <>
          <Spinner />
        </>
      ) : (
        <div>
          <div className="">
            <div className="mt-5"></div>
            <MultiSelectSearchDropDown
              prefix={<SearchIcon />}
              showSelectedItems={false}
              value={selectedUsersList}
              onChange={addUsers}
              options={usersOptions}
              searchFilter={userSearchFilter}
              optionItemSkin={(user) => (
                <div className="">
                  <p className="text-sm">{user.given_name}</p>
                  <p className="font-light text-xs">{user.email}</p>
                </div>
              )}
            />

            <div className="mt-5">
              <h4 className="text-left text-bold text-gray-700 ">
                Give access To
              </h4>
              <div className="my-4 flex flex-wrap gap-2.5">
                {selectedUsersList.map((user) => (
                  <Pill.Delete
                    children={user.given_name}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      removeUser(user.id);
                    }}
                  />
                ))}
                <p></p>
              </div>
            </div>

            <div className=" inline-flex w-full justify-center">
              <div className="w-1/2">
                <CustomButton
                  bgColor="transparent"
                  textColor="gray-600"
                  text="Cancel"
                  borderColor="gray-100"
                  onClick={closePopup}
                />
              </div>
              <div className="ml-5 w-1/2 ">
                <CustomButton
                  bgColor="green"
                  borderColor="green"
                  textColor="white"
                  text="Share"
                  onClick={editPermissions}
                />
              </div>
            </div>
            <div className="mt-4">
              <CheckboxInput
                label="Share with Public Link"
                checked={enablePublicShare}
                onChange={updateSharableState}
              />
            </div>
          </div>

          {enablePublicShare && (
            <div className="p-4 bg-gray-dark bg-opacity-5">
              <div className="flex items-center  justify-start">
                <ShareFolderIcon />

                <div className="ml-2.5 text-left">
                  <p className="text-medium text-black font-semibold">
                    Get Link
                  </p>
                  <p className="text-gray-500 text-sm">
                    Allow external users to view the folder.
                  </p>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap justify-between gap-2">
                <p className="p-3 text-xs text-gray-700 border border-gray-100 flex justify-center items-center rounded-xl truncate">
                  {publicURL}
                </p>

                <Button onClick={copyShareableLink} theme="gray-fill">
                  Copy Link
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </OverlayWindow>
  );
};

export default ShareModal;
